import * as React from 'react'
import { toast } from 'sonner'

/**
 * Registers the service worker and offers the reload when a new build lands.
 *
 * The problem this solves: this is a browser app, so a deploy moves nobody. A
 * tab open since Thursday keeps running Thursday's JavaScript against today's
 * API, and a phone that has been asleep for a fortnight wakes up on a fortnight-old
 * bundle. `users.app_version` was added to MEASURE that; this is the half that
 * fixes it.
 *
 * Deliberately a prompt, never an automatic reload. Reloading out from under
 * someone discards a half-typed destination or a dialog they had open, and the
 * app has no draft persistence to soften that. The toast is persistent rather
 * than timed for the same reason — a five-second window is not a choice.
 */

/** How often an open tab asks whether a new build exists. */
const UPDATE_CHECK_INTERVAL_MS = 15 * 60_000

/** One id, so re-checks replace the toast instead of stacking copies of it. */
const TOAST_ID = 'app-update-available'

export function ServiceWorkerUpdater() {
  React.useEffect(() => {
    if (typeof window === 'undefined') return
    if (!('serviceWorker' in navigator)) return
    /*
     * Development is excluded on purpose: the dev server serves unbundled
     * modules that a cached worker would fight with, and the resulting "why is
     * my change not showing" is a genuinely miserable hour.
     */
    if (!import.meta.env.PROD) return

    let registration: ServiceWorkerRegistration | null = null
    let interval: ReturnType<typeof setInterval> | null = null
    let reloading = false

    /* The reload happens on controllerchange rather than immediately after
       postMessage, because the new worker has to finish activating first —
       reloading earlier just serves the old bundle again. */
    const onControllerChange = () => {
      if (reloading) return
      reloading = true
      window.location.reload()
    }

    const promptToReload = (waiting: ServiceWorker) => {
      toast('A new version of Constellation is available.', {
        id: TOAST_ID,
        duration: Infinity,
        action: {
          label: 'Reload',
          onClick: () => waiting.postMessage({ type: 'SKIP_WAITING' }),
        },
      })
    }

    const watch = (current: ServiceWorkerRegistration) => {
      /* Already waiting when the page loaded — the update arrived while this
         tab was closed or on a previous navigation. */
      if (current.waiting && navigator.serviceWorker.controller) {
        promptToReload(current.waiting)
      }

      current.addEventListener('updatefound', () => {
        const installing = current.installing
        if (!installing) return

        installing.addEventListener('statechange', () => {
          /* `controller` distinguishes an UPDATE from a FIRST install: on a
             first install there is nothing to reload away from, and prompting
             would be nonsense. */
          if (installing.state === 'installed' && navigator.serviceWorker.controller) {
            promptToReload(installing)
          }
        })
      })
    }

    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange)

    navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .then((current) => {
        registration = current
        watch(current)

        /* Two triggers, because tabs are used differently: an interval catches
           the monitor left open all week, and focus catches the laptop that was
           shut and reopened — which the interval alone handles badly, since
           timers in a suspended tab are throttled hard. */
        interval = setInterval(() => {
          void current.update().catch(() => {
            /* Offline, or the check raced a deploy. The next one will do. */
          })
        }, UPDATE_CHECK_INTERVAL_MS)
      })
      .catch(() => {
        /* A failed registration must never break the app: the worker is an
           enhancement, and the page works fine without it. */
      })

    const onFocus = () => {
      void registration?.update().catch(() => {})
    }
    window.addEventListener('focus', onFocus)

    return () => {
      window.removeEventListener('focus', onFocus)
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange)
      if (interval) clearInterval(interval)
    }
  }, [])

  return null
}
